import { useState, useRef, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Loader2, X, Check, AlertCircle, Trash2, FileType2 } from "lucide-react";
import StateSelector from "@/components/StateSelector";
import UserSelector from "@/components/UserSelector";
import ModalShell from "@/components/ModalShell";

const CATEGORIES = ["unassigned", "product", "strain", "marketing", "retail", "sales", "merch"];

function cleanFilename(filename) {
  return filename.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
}

function toTitleCase(str) {
  return str
    .split(" ")
    .map((w) => (w.length > 2 ? w.charAt(0).toUpperCase() + w.slice(1) : w.toUpperCase()))
    .join(" ");
}

function deriveTitle(filename, opts = {}) {
  const base = toTitleCase(cleanFilename(filename));
  const parts = [base];
  const states = (opts.states || "").split(",").map((s) => s.trim()).filter(Boolean);
  if (states.length) parts.push(states.join("/"));
  else if (opts.category && opts.category !== "unassigned") {
    parts.push(opts.category.charAt(0).toUpperCase() + opts.category.slice(1));
  }
  return parts.join(" — ");
}

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function BulkUploadModal({ open, onClose, defaultCategory = "marketing", defaultFolderId = null, onUploaded }) {
  const fileRef = useRef(null);
  const [items, setItems] = useState([]); // {id, file, title, status: 'pending'|'uploading'|'done'|'error', error}
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const initialCategory = defaultCategory || "unassigned";
  const [shared, setShared] = useState({
    category: initialCategory,
    subcategory: "",
    tags: "",
    states: "",
    allowed_user_ids: "",
    description: "",
  });

  useEffect(() => {
    if (open) {
      setShared((s) => ({ ...s, category: defaultCategory || "unassigned" }));
    } else {
      setItems([]);
      setShared({ category: defaultCategory || "unassigned", subcategory: "", tags: "", states: "", allowed_user_ids: "", description: "" });
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [open, defaultCategory]);

  if (!open) return null;

  function addFiles(fileList) {
    const arr = Array.from(fileList);
    const next = arr.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      title: deriveTitle(f.name, { category: shared.category, states: shared.states }),
      auto: true, // title was auto-generated; re-derive when shared changes
      status: "pending",
      error: null,
    }));
    setItems((prev) => [...prev, ...next]);
  }

  function removeItem(id) {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }

  function updateTitle(id, title) {
    // User-edited title — pin it so shared updates won't overwrite
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, title, auto: false } : i)));
  }

  function updateShared(patch) {
    const nextShared = { ...shared, ...patch };
    setShared(nextShared);
    // Re-derive any titles still on auto so they pick up the new category/location
    setItems((prev) =>
      prev.map((i) =>
        i.auto
          ? { ...i, title: deriveTitle(i.file.name, { category: nextShared.category, states: nextShared.states }) }
          : i
      )
    );
  }

  async function uploadAll() {
    if (items.length === 0) return toast.error("Add files first");
    setBusy(true);
    let successCount = 0;
    let failCount = 0;
    for (const item of items) {
      if (item.status === "done") continue;
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: "uploading", error: null } : i)));
      const fd = new FormData();
      fd.append("file", item.file);
      fd.append("title", item.title || deriveTitle(item.file.name));
      fd.append("description", shared.description);
      fd.append("category", shared.category);
      fd.append("subcategory", shared.subcategory);
      fd.append("tags", shared.tags);
      fd.append("states", shared.states);
      fd.append("allowed_user_ids", shared.allowed_user_ids);
      if (defaultFolderId) fd.append("folder_id", defaultFolderId);
      try {
        await api.post("/admin/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: "done" } : i)));
        successCount++;
      } catch (err) {
        setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: "error", error: formatApiError(err) } : i)));
        failCount++;
      }
    }
    setBusy(false);
    if (successCount) toast.success(`${successCount} asset${successCount > 1 ? "s" : ""} uploaded`);
    if (failCount) toast.error(`${failCount} failed`);
    if (successCount) onUploaded && onUploaded();
    // Auto-close only if everything succeeded
    if (failCount === 0 && successCount > 0) {
      setTimeout(onClose, 400);
    }
  }

  const pendingCount = items.filter((i) => i.status !== "done").length;
  const doneCount = items.filter((i) => i.status === "done").length;

  return (
    <ModalShell open={open} onClose={busy ? () => {} : onClose} maxWidth="max-w-3xl" testId="bulk-upload-modal">
      <button onClick={onClose} disabled={busy} className="absolute top-4 right-4 p-1 hover:bg-surface-elev rounded-sm disabled:opacity-30" data-testid="bulk-close" aria-label="Close">
        <X className="h-4 w-4" />
      </button>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Bulk Upload</div>
      <h2 className="font-display text-3xl uppercase font-bold tracking-tight">Upload Many Assets</h2>
        <p className="text-sm text-muted-foreground mt-2">Drop multiple files at once. The metadata below applies to all — titles are auto-generated from filenames and editable per file.</p>

        {/* Dropzone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
          className={`mt-6 border-2 border-dashed rounded-sm p-8 text-center transition ${drag ? "border-foreground bg-surface-elev" : "border-border"}`}
        >
          <UploadCloud className="h-7 w-7 mx-auto text-muted-foreground" />
          <div className="mt-3 text-sm">Drag & drop files here, or</div>
          <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="bulk-file-input" />
          <button type="button" onClick={() => fileRef.current?.click()} className="mt-3 border border-border px-4 py-1.5 text-xs uppercase tracking-widest font-mono hover:bg-surface-elev transition" data-testid="bulk-pick">
            Select Multiple Files
          </button>
        </div>

        {/* Shared metadata */}
        {items.length > 0 && (
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <Field label="Category (shared)">
              <select value={shared.category} onChange={(e) => updateShared({ category: e.target.value })} data-testid="bulk-cat" className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground capitalize">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Subcategory (optional)">
              <input value={shared.subcategory} onChange={(e) => updateShared({ subcategory: e.target.value })} data-testid="bulk-sub" className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground" />
            </Field>
            <Field label="Tags (comma-separated)">
              <input value={shared.tags} onChange={(e) => updateShared({ tags: e.target.value })} data-testid="bulk-tags" placeholder="summer, retail" className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground" />
            </Field>
          </div>
        )}

        {items.length > 0 && (
          <div className="mt-4 space-y-4">
            <StateSelector
              value={shared.states}
              onChange={(str) => updateShared({ states: str })}
              testIdPrefix="bulk-state"
            />
            <UserSelector
              value={shared.allowed_user_ids}
              onChange={(str) => updateShared({ allowed_user_ids: str })}
              testIdPrefix="bulk-users"
            />
          </div>
        )}

        {/* File queue */}
        {items.length > 0 && (
          <div className="mt-6 border border-border rounded-sm divide-y divide-border max-h-[280px] overflow-y-auto" data-testid="bulk-queue">
            {items.map((i) => (
              <div key={i.id} className="flex items-center gap-3 p-3">
                <FileType2 className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <input
                    value={i.title}
                    onChange={(e) => updateTitle(i.id, e.target.value)}
                    disabled={i.status === "uploading" || i.status === "done"}
                    className="w-full bg-transparent text-sm outline-none disabled:opacity-60"
                    data-testid={`bulk-title-${i.id}`}
                  />
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5 truncate">
                    {i.file.name} · {formatSize(i.file.size)}
                    {i.error && <span className="ml-2 text-[#E15C2C]">— {i.error}</span>}
                  </div>
                </div>
                <StatusIcon status={i.status} />
                {i.status !== "uploading" && (
                  <button onClick={() => removeItem(i.id)} className="p-1 hover:bg-surface-elev rounded-sm text-muted-foreground hover:text-foreground" data-testid={`bulk-remove-${i.id}`} aria-label="Remove">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              {items.length} file{items.length > 1 ? "s" : ""} · {doneCount} done · {pendingCount} pending
            </div>
            <button
              onClick={uploadAll}
              disabled={busy || pendingCount === 0}
              className="bg-foreground text-background py-3 px-6 uppercase tracking-widest text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
              data-testid="bulk-submit"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Upload {pendingCount} {pendingCount === 1 ? "Asset" : "Assets"}
            </button>
          </div>
        )}
    </ModalShell>
  );
}

function StatusIcon({ status }) {
  if (status === "uploading") return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  if (status === "done") return <Check className="h-4 w-4" style={{ color: "#FBC90C" }} />;
  if (status === "error") return <AlertCircle className="h-4 w-4" style={{ color: "#E15C2C" }} />;
  return <span className="w-4 h-4" />;
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1 block">{label}</label>
      {children}
    </div>
  );
}
